import {
  selectDidHierarchySaveFail,
  selectIsSavingHierarchyChanges,
} from "@/data-preparation-tool/store/data-preparation-ui/draft-saving-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  Checkmark2Icon,
  FaroText,
  FaroTooltip,
  grey,
  UpdateIcon,
} from "@faro-lotv/flat-ui";
import { Fade, Stack } from "@mui/material";
import { useEffect, useRef, useState, type JSX } from "react";

/** Duration in ms to show the saved checkmark after a hierarchy save completes. */
const SHOW_SAVING_COMPLETED_DURATION = 5000;

/** Styles for the indicator container */
const INDICATOR_SX = {
  flexDirection: "row",
  alignItems: "center",
  gap: 0.5,
  color: grey[600],
};

/**
 * @returns An indicator showing while Draft View hierarchy changes are saving.
 */
export function DraftSavingIndicator(): JSX.Element {
  const isSavingHierarchyChanges = useAppSelector(
    selectIsSavingHierarchyChanges,
  );
  const didHierarchySaveFail = useAppSelector(selectDidHierarchySaveFail);

  // Ref to track the previous value of isSavingHierarchyChanges, to detect when it transitions from true to false.
  const wasSavingHierarchyChanges = useRef(isSavingHierarchyChanges);

  const [showSavingCompleted, setShowSavingCompleted] = useState(false);

  useEffect(() => {
    setShowSavingCompleted(
      !isSavingHierarchyChanges &&
        wasSavingHierarchyChanges.current &&
        !didHierarchySaveFail,
    );

    wasSavingHierarchyChanges.current = isSavingHierarchyChanges;
  }, [isSavingHierarchyChanges, didHierarchySaveFail]);

  useEffect(() => {
    if (showSavingCompleted) {
      const timeout = setTimeout(() => {
        setShowSavingCompleted(false);
      }, SHOW_SAVING_COMPLETED_DURATION);

      return () => clearTimeout(timeout);
    }
  }, [showSavingCompleted]);

  return (
    <Fade in={isSavingHierarchyChanges || showSavingCompleted} unmountOnExit>
      {isSavingHierarchyChanges ? (
        <FaroTooltip title="Saving changes to the cloud. Don't close your browser.">
          <Stack aria-label="draft saving indicator" sx={INDICATOR_SX}>
            <UpdateIcon fontSize="small" />
            <FaroText variant="bodyS">Saving...</FaroText>
          </Stack>
        </FaroTooltip>
      ) : (
        <FaroTooltip title="Changes saved to the cloud. It's now safe to close your browser.">
          <Stack aria-label="draft saved indicator" sx={INDICATOR_SX}>
            <Checkmark2Icon fontSize="small" />
            <FaroText variant="bodyS">Saved</FaroText>
          </Stack>
        </FaroTooltip>
      )}
    </Fade>
  );
}
